import {FieldFilter} from '@6grain/masofa-monolith-api-client/dictionaries';
import {Bid} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {CustomAnomalyPolygonsViewModel} from '@6grain/masofa-monolith-api-client/satellite-indices';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';
import {parseFilterNumericValue} from '@app/utils/_msf';
import {FilterOperator} from '@app/utils/filter-operator';

/** Query / UI key. Not an AnomalyPolygon property — never send this to BaseGetQuery. */
export const ANOMALIES_MAP_BID_NUMBER_QUERY_KEY = 'LinkedBidNumber';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function fieldFilter(field: string, operator: FilterOperator, value: unknown): FieldFilter {
  return {
    filterField: field,
    filterOperator: operator as FieldFilter['filterOperator'],
    filterValue: value,
  };
}

/** Empty / missing / invalid → no filter (show all). Positive integer → resolve bid, then Id/BidId. */
export function parseLinkedBidNumber(raw: unknown): number | null {
  const n = parseFilterNumericValue(raw);
  if (n == null || !Number.isInteger(n) || n <= 0) {
    return null;
  }
  return n;
}

export function readLinkedBidNumberFromQuery(
  params: {get(name: string): string | null},
): number | null {
  return parseLinkedBidNumber(
    params.get(ANOMALIES_MAP_BID_NUMBER_QUERY_KEY) ?? params.get('number'),
  );
}

export function buildLinkedBidNumberFilterField(): IMsfFormBuilder {
  return {
    name: ANOMALIES_MAP_BID_NUMBER_QUERY_KEY,
    title: 'Request number',
    type: 'input-number',
    placeholder: 'Enter request number',
  };
}

export function createBidNumberEqualsQuery(bidNumber: number): {
  take: number;
  offset: number;
  filters: FieldFilter[];
} {
  return {
    take: 1,
    offset: 0,
    filters: [fieldFilter('number', FilterOperator.Equals, bidNumber)],
  };
}

export function createPolygonsByBidIdQuery(bidId: string): {
  take: number;
  offset: number;
  filters: FieldFilter[];
} {
  return {
    take: 200,
    offset: 0,
    filters: [
      fieldFilter('BidId', FilterOperator.Equals, bidId),
      fieldFilter('AnomalyOrigin', FilterOperator.Equals, 'Both'),
    ],
  };
}

export function pickFirstBid(bids: Bid[] | null | undefined): Bid | undefined {
  return Array.isArray(bids) ? bids[0] : undefined;
}

export function anomalyIdsFromCustomGetRows(
  rows: CustomAnomalyPolygonsViewModel[] | null | undefined,
): Array<{id?: string | null}> {
  return (rows ?? [])
    .map(row => row?.anomalyPolygon)
    .filter((polygon): polygon is NonNullable<typeof polygon> => !!polygon);
}

/** No matching bid → empty list/map, not “all anomalies”. */
export function emptyAnomalyMatchFilter(): FieldFilter {
  return fieldFilter('Id', FilterOperator.Equals, EMPTY_GUID);
}

export function collectLinkedAnomalyIds(
  bid: {anomalyPolygonId?: string | null},
  polygons: Array<{id?: string | null} | null | undefined>,
): string[] {
  const ids = new Set<string>();
  const parent = String(bid.anomalyPolygonId ?? '').trim();
  if (parent) {
    ids.add(parent);
  }
  for (const polygon of polygons) {
    const id = String(polygon?.id ?? '').trim();
    if (id) {
      ids.add(id);
    }
  }
  return [...ids];
}

/**
 * AnomalyPolygon-only fields: {@code Id} when we know polygon ids,
 * otherwise {@code BidId} (entity column, not LinkedBidNumber).
 */
export function anomalyPolygonFilterForLinkedBid(opts: {
  bidId: string;
  anomalyIds: string[];
}): FieldFilter {
  const unique = [...new Set(opts.anomalyIds.map(id => id.trim()).filter(Boolean))];
  if (unique.length === 1) {
    return fieldFilter('Id', FilterOperator.Equals, unique[0]);
  }
  if (unique.length > 1) {
    return fieldFilter('Id', FilterOperator.AnyOf, unique);
  }
  return fieldFilter('BidId', FilterOperator.Equals, opts.bidId);
}
